import React, { useEffect, useState } from 'react';
import { Box, Flex, Button, Text, Card } from 'rebass/styled-components';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { colors } from '../../util/theme';
import CreateUser from './CreateUser';
import Overview from './Overview';
import UsersList from './UsersList';
import UsersManager from './UsersManager';
import ActivityLog from './ActivityLog';
import SearchSettings from './SearchSettings';
import OrgSettings from './OrgSettings';
import { SettingsCard, SaveRow, fieldLabel, textInput } from './SettingsCard';
import useAuth from 'hooks/useAuth';
import { adminFunctionsEnabled } from 'util/adminApi';
import { searchProvider } from 'hooks/useSearch';
import { useFirestore } from 'react-redux-firebase';
import { firestoreCollection } from 'util/constants';
import { DefaultsType } from './types';
import useDefaultFieldsListener from 'hooks/useDefaultFieldsListener';
import useAdminStats from 'hooks/useAdminStats';
import { AppState } from 'store';

const Tab = styled(Box)<{ active: boolean }>`
  cursor: pointer;
  padding: 8px 4px;
  margin-right: 24px;
  font-weight: ${(p) => (p.active ? 600 : 300)};
  color: ${(p) => (p.active ? colors.primary : colors.grey3)};
  border-bottom: 2px solid ${(p) => (p.active ? colors.primary : 'transparent')};
  user-select: none;
`;

type TabKey = 'overview' | 'users' | 'activity' | 'settings';

const DEFAULT_FIELDS: { name: keyof DefaultsType; label: string }[] = [
  { name: 'title', label: 'Title' },
  { name: 'projectNumber', label: 'Project number' },
  { name: 'client', label: 'Client' },
  { name: 'agency', label: 'Agency' },
];

const Admin = () => {
  const [tab, setTab] = useState<TabKey>('overview');
  const { stats, loading, error, reload } = useAdminStats();
  const { user } = useSelector((state: AppState) => state.auth);
  const { role, user: authUser } = useAuth();

  const [projectDefaults, setProjectDefaults] = useState<DefaultsType>({
    title: '',
    projectNumber: '',
    client: '',
    agency: '',
  });
  const [isEmpty, setIsEmpty] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const { defaults } = useDefaultFieldsListener();
  const firestore = useFirestore();

  const handleDefaultsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setSaved(null);
      setProjectDefaults((pre) => ({ ...pre, [name]: value }));
    }
  };

  useEffect(() => {
    if (defaults) setProjectDefaults(defaults);
  }, [defaults]);

  const updateDefaultFields = async () => {
    try {
      const collectionRef = firestore.collection(firestoreCollection.projectDefaults);
      const snapshot = await collectionRef.limit(1).get();
      if (!snapshot.empty) {
        await collectionRef.doc(snapshot.docs[0].id).update(projectDefaults);
        setSaved('Saved');
      } else {
        setSaved('No defaults document found — nothing was saved.');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      setSaved(`Save failed: ${error.message}`);
    }
  };

  useEffect(() => {
    setIsEmpty(DEFAULT_FIELDS.some((f) => !projectDefaults[f.name]));
  }, [projectDefaults]);

  return (
    <Box p={[2, 3]} sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Flex alignItems="center" mb={3} flexWrap="wrap" sx={{ gap: '8px' }}>
        <Text fontSize={4} fontWeight={2} color="greyDark" mr={3}>
          Admin Dashboard
        </Text>
        <Box flex={1} />
        {stats && (
          <Text fontSize={0} color="grey3">
            Updated {stats.loadedAt.toLocaleTimeString()}
          </Text>
        )}
        <Button variant="primary" onClick={reload} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Flex>

      <Flex mb={3} sx={{ borderBottom: '1px solid', borderColor: 'grey2' }}>
        <Tab active={tab === 'overview'} onClick={() => setTab('overview')}>
          Overview
        </Tab>
        <Tab active={tab === 'users'} onClick={() => setTab('users')}>
          Users
        </Tab>
        {adminFunctionsEnabled && (
          <Tab active={tab === 'activity'} onClick={() => setTab('activity')}>
            Activity
          </Tab>
        )}
        <Tab active={tab === 'settings'} onClick={() => setTab('settings')}>
          Settings
        </Tab>
      </Flex>

      {error && (
        <Card p={3} mb={3} bg="white" sx={{ borderLeft: '4px solid', borderColor: 'primary' }}>
          <Text color="primary">Couldn't load the dashboard figures: {error}</Text>
        </Card>
      )}

      {(tab === 'overview' || (tab === 'users' && !adminFunctionsEnabled)) && !stats && loading && (
        <Text color="grey3">Loading figures from the database…</Text>
      )}

      {tab === 'overview' && stats && <Overview stats={stats} />}
      {tab === 'users' && adminFunctionsEnabled && <UsersManager currentUid={authUser?.uid} currentRole={role} />}
      {tab === 'users' && !adminFunctionsEnabled && stats && <UsersList users={stats.users} currentEmail={user?.email} />}
      {tab === 'activity' && adminFunctionsEnabled && <ActivityLog />}

      {tab === 'settings' && (
        <>
          {adminFunctionsEnabled && <OrgSettings />}
          {/* Same rounded box as the sections above (Aaron, 5 Nov 2026). */}
          <SettingsCard id="defaults-h" title="Project field defaults" note="The labels shown for these fields on every project.">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
              {DEFAULT_FIELDS.map((f) => (
                <label key={f.name} style={fieldLabel}>
                  {f.label}
                  <input style={textInput} onChange={handleDefaultsChange} name={f.name} value={projectDefaults[f.name]} type="text" />
                </label>
              ))}
            </div>
            <SaveRow
              onSave={updateDefaultFields}
              disabled={isEmpty}
              message={saved || (isEmpty ? 'Fill in all four labels to save.' : '')}
            />
          </SettingsCard>

          {adminFunctionsEnabled && searchProvider === 'builtin' && <SearchSettings />}

          {!adminFunctionsEnabled && (
          <Box mb={4}>
            <Card
              p={3}
              mb={2}
              bg="white"
              sx={{ borderLeft: '4px solid', borderColor: 'secondary', borderRadius: 4 }}
            >
              <Text fontSize={1} color="greyDark">
                Adding a user from here is expected to sign you out and into the new account (Firebase signs in
                whoever it creates from the browser). A safer server-side version is planned for phase 2.
              </Text>
            </Card>
            <CreateUser />
          </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Admin;
